import React, { useState, useRef, useEffect, useCallback } from 'react';
import { BackIcon, TrashIcon } from './icons';
import { a11y } from '../utils/a11yLabels';
import DialogCard from './DialogCard';
import apiClient from '../api/client';

// Review screen shown when a photo yields SEVERAL events (a schedule/programme).
// The user checks/edits the list, then creates them all in one go.
const PRIO_CYCLE = ['green', 'yellow', 'red'];
const PRIO_COLOR = { green: '#4E7A52', yellow: '#E0A83E', red: '#D04535' };

// How many events are sent to the server at the same time. One at a time made
// a 12-event flyer take a dozen round trips end to end; more than a handful at
// once and a slow phone starts dropping them.
const BATCH = 4;

function pad(n) { return String(n).padStart(2, '0'); }
function localDate(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function localTime(d) { return `${pad(d.getHours())}:${pad(d.getMinutes())}`; }

// Shown when some saves failed (e.g. the phone lost signal): the failed rows
// stay on screen and the main button retries just those.
const FAILED_LABELS = {
  it: (n) => `⚠️ ${n} eventi NON salvati (server non raggiungibile?). Riprova con il pulsante qui sotto.`,
  en: (n) => `⚠️ ${n} events NOT saved (server unreachable?). Tap the button below to retry.`,
  es: (n) => `⚠️ ${n} eventos NO guardados (¿servidor no disponible?). Reintenta con el botón de abajo.`,
  de: (n) => `⚠️ ${n} Termine NICHT gespeichert (Server nicht erreichbar?). Unten erneut versuchen.`,
  fr: (n) => `⚠️ ${n} événements NON enregistrés (serveur injoignable ?). Réessayez avec le bouton ci-dessous.`,
  nl: (n) => `⚠️ ${n} afspraken NIET opgeslagen (server onbereikbaar?). Probeer het hieronder opnieuw.`,
  pt: (n) => `⚠️ ${n} eventos NÃO guardados (servidor indisponível?). Tenta novamente com o botão abaixo.`,
  ru: (n) => `⚠️ ${n} событий НЕ сохранено (сервер недоступен?). Повторите кнопкой ниже.`,
  pl: (n) => `⚠️ ${n} wydarzeń NIE zapisano (serwer niedostępny?). Spróbuj ponownie przyciskiem poniżej.`,
  ro: (n) => `⚠️ ${n} evenimente NESALVATE (server indisponibil?). Reîncearcă cu butonul de mai jos.`,
  zh: (n) => `⚠️ ${n} 个日程未保存（服务器无法连接？）。请点击下方按钮重试。`,
  ja: (n) => `⚠️ ${n} 件の予定が保存されませんでした（サーバーに接続できない可能性）。下のボタンで再試行してください。`,
  hi: (n) => `⚠️ ${n} इवेंट सहेजे नहीं गए (सर्वर उपलब्ध नहीं?)। नीचे दिए बटन से फिर से प्रयास करें।`,
  ko: (n) => `⚠️ ${n}개 일정이 저장되지 않았습니다 (서버 연결 불가?). 아래 버튼으로 다시 시도하세요.`,
  ar: (n) => `⚠️ ${n} أحداث لم تُحفظ (تعذّر الوصول للخادم؟). أعد المحاولة بالزر أدناه.`,
};

// A date the phone cannot make sense of is NOT a network problem. Saying
// "server unreachable" for it sent the user hunting for a signal while the
// real fix was one field away, on one specific row.
const BAD_DATE_LABELS = {
  it: (n) => `⚠️ ${n} riga/e ha una data che non riesco a leggere: correggila qui sotto.`,
  en: (n) => `⚠️ ${n} row(s) have a date I can't read: fix it below.`,
  es: (n) => `⚠️ ${n} fila(s) tienen una fecha que no puedo leer: corrígela abajo.`,
  de: (n) => `⚠️ ${n} Zeile(n) haben ein unlesbares Datum: bitte unten korrigieren.`,
  fr: (n) => `⚠️ ${n} ligne(s) ont une date illisible : corrigez-la ci-dessous.`,
  nl: (n) => `⚠️ ${n} rij(en) hebben een onleesbare datum: pas die hieronder aan.`,
  pt: (n) => `⚠️ ${n} linha(s) têm uma data ilegível: corrige-a abaixo.`,
  ru: (n) => `⚠️ В ${n} строке(ах) нечитаемая дата: исправьте её ниже.`,
  pl: (n) => `⚠️ ${n} wiersz(y) ma nieczytelną datę: popraw ją poniżej.`,
  ro: (n) => `⚠️ ${n} rând(uri) au o dată ilizibilă: corectează mai jos.`,
  zh: (n) => `⚠️ 有 ${n} 行的日期无法识别，请在下方修改。`,
  ja: (n) => `⚠️ ${n} 行の日付が読み取れません。下で修正してください。`,
  hi: (n) => `⚠️ ${n} पंक्ति की तारीख़ पढ़ी नहीं जा सकी: नीचे ठीक करें।`,
  ko: (n) => `⚠️ ${n}개 행의 날짜를 인식할 수 없습니다. 아래에서 수정하세요.`,
  ar: (n) => `⚠️ ${n} صف/صفوف بها تاريخ غير مقروء: صحّحه أدناه.`,
};

// Per-row markers. Short on purpose: they sit inside the card, under the title.
const ROW_HINTS = {
  it: { noTitle: 'Manca il titolo', badDate: 'Data non valida', failed: 'Non salvato' },
  en: { noTitle: 'Title missing',   badDate: 'Invalid date',    failed: 'Not saved'   },
  es: { noTitle: 'Falta el título', badDate: 'Fecha no válida', failed: 'No guardado' },
  de: { noTitle: 'Titel fehlt',     badDate: 'Ungültiges Datum', failed: 'Nicht gespeichert' },
  fr: { noTitle: 'Titre manquant',  badDate: 'Date invalide',   failed: 'Non enregistré' },
  nl: { noTitle: 'Titel ontbreekt', badDate: 'Ongeldige datum', failed: 'Niet opgeslagen' },
  pt: { noTitle: 'Falta o título',  badDate: 'Data inválida',   failed: 'Não guardado' },
  ru: { noTitle: 'Нет названия',    badDate: 'Неверная дата',   failed: 'Не сохранено' },
  pl: { noTitle: 'Brak tytułu',     badDate: 'Błędna data',     failed: 'Niezapisane'  },
  ro: { noTitle: 'Lipsește titlul', badDate: 'Dată invalidă',   failed: 'Nesalvat'     },
  zh: { noTitle: '缺少标题',         badDate: '日期无效',         failed: '未保存'        },
  ja: { noTitle: 'タイトルなし',      badDate: '日付が無効',        failed: '未保存'        },
  hi: { noTitle: 'शीर्षक नहीं है',     badDate: 'तारीख़ ग़लत',       failed: 'सहेजा नहीं गया' },
  ko: { noTitle: '제목 없음',        badDate: '날짜 오류',         failed: '저장 안 됨'     },
  ar: { noTitle: 'العنوان ناقص',    badDate: 'تاريخ غير صالح',   failed: 'لم يُحفظ'      },
};

// Leaving this screen throws away everything the photo found. Worth one question.
const DISCARD_LABELS = {
  it: { text: (n) => `Vuoi buttare via i ${n} eventi letti dalla foto?`, leave: 'Sì, esci', stay: 'No, resta' },
  en: { text: (n) => `Discard the ${n} events read from the photo?`,     leave: 'Yes, leave', stay: 'No, stay' },
  es: { text: (n) => `¿Descartar los ${n} eventos leídos de la foto?`,   leave: 'Sí, salir', stay: 'No, quedarme' },
  de: { text: (n) => `Die ${n} erkannten Termine verwerfen?`,            leave: 'Ja, verlassen', stay: 'Nein, bleiben' },
  fr: { text: (n) => `Abandonner les ${n} événements lus sur la photo ?`, leave: 'Oui, quitter', stay: 'Non, rester' },
  nl: { text: (n) => `De ${n} herkende afspraken weggooien?`,            leave: 'Ja, sluiten', stay: 'Nee, blijven' },
  pt: { text: (n) => `Descartar os ${n} eventos lidos da foto?`,         leave: 'Sim, sair', stay: 'Não, ficar' },
  ru: { text: (n) => `Отменить ${n} распознанных событий?`,              leave: 'Да, выйти', stay: 'Нет, остаться' },
  pl: { text: (n) => `Odrzucić ${n} rozpoznanych wydarzeń?`,             leave: 'Tak, wyjdź', stay: 'Nie, zostań' },
  ro: { text: (n) => `Renunți la cele ${n} evenimente citite din poză?`, leave: 'Da, ieși', stay: 'Nu, rămân' },
  zh: { text: (n) => `要放弃从照片识别出的 ${n} 个日程吗？`,                 leave: '是，离开', stay: '否，留下' },
  ja: { text: (n) => `写真から読み取った ${n} 件を破棄しますか？`,            leave: 'はい', stay: 'いいえ' },
  hi: { text: (n) => `फ़ोटो से पढ़े गए ${n} इवेंट हटा दें?`,                  leave: 'हाँ, बाहर', stay: 'नहीं, रुकें' },
  ko: { text: (n) => `사진에서 읽은 ${n}개 일정을 버릴까요?`,                leave: '예, 나가기', stay: '아니요' },
  ar: { text: (n) => `هل تريد تجاهل ${n} حدثًا مقروءًا من الصورة؟`,        leave: 'نعم، اخرج', stay: 'لا، ابقَ' },
};

// Cancel-mid-save label (the button turns into a stop while creating)
const STOP_LABELS = {
  it: 'Ferma', en: 'Stop', es: 'Parar', de: 'Stopp', fr: 'Arrêter', nl: 'Stoppen',
  pt: 'Parar', ru: 'Стоп', pl: 'Zatrzymaj', ro: 'Oprește', zh: '停止', ja: '停止',
  hi: 'रोकें', ko: '중지', ar: 'إيقاف',
};

let ROW_SEQ = 0;   // stable row ids — see the note on <Row> below

// One row of the list. Extracted and memoized: without this, typing a single
// letter in one title re-rendered every card of a 15-event flyer (each with a
// date input, a time input and a textarea), and correcting a title felt gluey.
const Row = React.memo(function Row({ row, hints, T, A, disabled, onPatch, onRemove, onCyclePrio }) {
  const S = 'var(--color-primary)';
  const r = row;
  const noTitle = !r.title.trim();
  // A row that cannot be created must not keep looking selected — the counter
  // in the header said 7 while eight rows showed a green tick.
  const willBeCreated = r.include && !noTitle && !!r.date;
  const problem = r.error === 'bad_date' ? hints.badDate
    : r.error === 'failed' ? hints.failed
    : (r.include && noTitle) ? hints.noTitle
    : null;

  return (
    <div style={{
      background: 'var(--color-card)',
      border: `2px solid ${problem ? '#D04535' : (willBeCreated ? S : 'var(--color-border)')}`,
      borderRadius: 'var(--radius-sm, 10px)', padding: 10, marginBottom: 10,
      opacity: r.include ? 1 : 0.55,
    }}>
      {/* top row: include + title + remove */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button type="button" disabled={disabled} onClick={() => onPatch(r.id, { include: !r.include })}
          aria-label={A.include} aria-pressed={willBeCreated}
          className="tap-grow"
          style={{ '--tap-x': '4px', flexShrink: 0, width: 22, height: 22, borderRadius: 6, border: `2px solid ${willBeCreated ? S : 'var(--color-border)'}`, background: willBeCreated ? S : 'transparent', color: '#fff', cursor: 'pointer', padding: 0, fontSize: '0.8rem', lineHeight: 1 }}>
          {willBeCreated ? '✓' : ''}
        </button>
        <input value={r.title} disabled={disabled} onChange={e => onPatch(r.id, { title: e.target.value, error: null })}
          style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: '0.95rem', fontWeight: 600, color: 'var(--color-text)', outline: 'none', fontFamily: 'inherit' }} />
        <button type="button" disabled={disabled} onClick={() => onRemove(r.id)} aria-label={`${T.form?.deleteButton || 'Remove'}: ${r.title || ''}`.trim()}
          style={{ flexShrink: 0, background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: 'var(--color-primary)', display: 'flex' }}><TrashIcon size={16} /></button>
      </div>

      {/* Why this row will not be created — right where the row is */}
      {problem && (
        <div style={{ marginTop: 4, marginInlineStart: 30, fontSize: '0.74rem', fontWeight: 700, color: '#D04535' }}>
          {problem}
        </div>
      )}

      {/* bottom row: date + time/allday + priority */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <input type="date" value={r.date} disabled={disabled} onChange={e => onPatch(r.id, { date: e.target.value, error: null })}
          style={{ border: `1px solid ${r.error === 'bad_date' ? '#D04535' : 'var(--color-border)'}`, borderRadius: 6, padding: '5px 7px', fontSize: '0.82rem', color: 'var(--color-text)', background: 'var(--color-card)', fontFamily: 'inherit' }} />
        {r.allDay ? (
          <button type="button" disabled={disabled} onClick={() => onPatch(r.id, { allDay: false })}
            style={{ border: '1px solid var(--color-border)', borderRadius: 6, padding: '5px 9px', fontSize: '0.8rem', background: 'var(--color-card)', color: 'var(--color-text-muted)', cursor: 'pointer', fontFamily: 'inherit' }}>
            {T.allDay}
          </button>
        ) : (
          <input type="time" value={r.time} disabled={disabled} onChange={e => onPatch(r.id, { time: e.target.value, error: null })} onBlur={e => { if (!e.target.value) onPatch(r.id, { allDay: true }); }}
            style={{ border: '1px solid var(--color-border)', borderRadius: 6, padding: '5px 7px', fontSize: '0.82rem', color: 'var(--color-text)', background: 'var(--color-card)', fontFamily: 'inherit' }} />
        )}
        <button type="button" disabled={disabled} onClick={() => onCyclePrio(r.id, r.priority)}
          aria-label={`${A.priority}: ${(T.form?.priorities || []).find(p => p.value === r.priority)?.label || r.priority}`}
          className="tap-grow"
          style={{ '--tap-x': '4px', marginLeft: 'auto', width: 24, height: 24, borderRadius: '50%', border: 'none', background: PRIO_COLOR[r.priority], cursor: 'pointer' }} />
      </div>

      {/* toggle: place & notes (per-event, editable before saving) */}
      <button type="button" disabled={disabled} onClick={() => onPatch(r.id, { open: !r.open })}
        style={{ marginTop: 8, background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: 'var(--color-text-muted)', fontSize: '0.8rem', fontFamily: 'inherit' }}>
        <span>📍 {T.multiDetails}{(r.location || r.notes) ? ' •' : ''}</span>
        <span style={{ fontSize: '0.7rem' }}>{r.open ? '▲' : '▾'}</span>
      </button>
      {r.open && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input value={r.location} disabled={disabled} onChange={e => onPatch(r.id, { location: e.target.value })}
            placeholder={T.locationPlaceholder || ''}
            style={{ border: '1px solid var(--color-border)', borderRadius: 6, padding: '7px 9px', fontSize: '0.85rem', color: 'var(--color-text)', background: 'var(--color-card)', fontFamily: 'inherit', outline: 'none' }} />
          <textarea value={r.notes} disabled={disabled} onChange={e => onPatch(r.id, { notes: e.target.value })}
            placeholder={T.notesPlaceholder || ''} rows={2}
            style={{ border: '1px solid var(--color-border)', borderRadius: 6, padding: '7px 9px', fontSize: '0.85rem', color: 'var(--color-text)', background: 'var(--color-card)', fontFamily: 'inherit', outline: 'none', resize: 'vertical' }} />
        </div>
      )}
    </div>
  );
});

export default function MultiEventReview({ events, timezone, T, lang = 'it', onCancel, onCreated,
  groups = [], userName = '', visibility: v0 = 'private', groupId: g0 = '',
  subCalendars = [], subCalendarId: sc0 = '' }) {
  // Where all of these are going. One choice for the whole batch, which is the
  // granularity that matches the situation: a photographed schedule belongs to
  // one calendar — the school year goes in the family calendar, the shifts go
  // in yours. It used to send visibility:'private', written into the code, so
  // eight events off a flyer could only ever land in your own diary and the
  // only way to move them was to open and edit each one.
  const [visibility, setVisibility] = React.useState(v0);
  const [groupId, setGroupId] = React.useState(g0 || (groups[0] && groups[0].id) || '');
  // One label for the batch, like the calendar. A photographed schedule is
  // usually all one thing — the swimming lessons, the school year — and
  // labelling it afterwards meant opening every event again.
  const [subCalendarId, setSubCalendarId] = React.useState(sc0 || '');
  const base = (lang || 'it').split('-')[0];
  const A = a11y(lang);
  const failedMsg  = FAILED_LABELS[base]   || FAILED_LABELS.en;
  const badDateMsg = BAD_DATE_LABELS[base] || BAD_DATE_LABELS.en;
  const hints      = ROW_HINTS[base]       || ROW_HINTS.en;
  const DL         = DISCARD_LABELS[base]  || DISCARD_LABELS.en;
  const stopLabel  = STOP_LABELS[base]     || STOP_LABELS.en;

  const [rows, setRows] = useState(() => (events || []).map(e => {
    const d = e && e.datetime ? new Date(e.datetime) : null;
    const valid = d && !isNaN(d.getTime());
    const allDay = e.allDay === true || !valid;
    return {
      // A STABLE id, not the array index. Rows used to be tracked by object
      // identity, so editing a row while the batch was saving made the app lose
      // track of it and create it twice; and index keys made the keyboard jump
      // to another field when a row above was deleted.
      id: ++ROW_SEQ,
      include: true,
      title: e.title || '',
      date: valid ? localDate(d) : localDate(new Date()),
      time: (!allDay && valid) ? localTime(d) : '',
      allDay,
      priority: ['red', 'yellow', 'green'].includes(e.priority) ? e.priority : 'green',
      location: e.location || '',
      notes: e.notes || '',
      type: e.type || null,
      error: null,          // null | 'bad_date' | 'failed'
      // Auto-expand the detail area when the scan already found a place/notes.
      open: !!(e.location || e.notes),
    };
  }));
  const [creating, setCreating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [failedCount, setFailedCount] = useState(0);   // could not be sent / server said no
  const [badDateCount, setBadDateCount] = useState(0); // a date this phone cannot parse
  const [askDiscard, setAskDiscard] = useState(false);
  const stopRef = useRef(false);   // set by the Stop button, read inside the loop

  const chosen = rows.filter(r => r.include && r.title.trim() && r.date);

  // Stable callbacks, so <Row> memoization actually holds.
  const patch = useCallback((id, p) => {
    setRows(rs => rs.map(r => (r.id === id ? { ...r, ...p } : r)));
  }, []);
  const remove = useCallback((id) => setRows(rs => rs.filter(r => r.id !== id)), []);
  const cyclePrio = useCallback((id, cur) => {
    setRows(rs => rs.map(r => (r.id === id ? { ...r, priority: PRIO_CYCLE[(PRIO_CYCLE.indexOf(cur) + 1) % 3] } : r)));
  }, []);

  // The Android back button used to walk out of the whole event form and take
  // the scanned list with it. Swallow one back press: it closes this screen
  // through the same "are you sure" question as the Cancel button.
  const requestClose = useCallback(() => {
    if (creating) return;
    if (rows.length === 0) { onCancel(); return; }
    setAskDiscard(true);
  }, [creating, rows.length, onCancel]);

  useEffect(() => {
    window.history.pushState({ memoMultiReview: true }, '');
    const onPop = () => {
      // Put the entry back, so a second back press asks again instead of leaving.
      window.history.pushState({ memoMultiReview: true }, '');
      requestClose();
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [requestClose]);

  async function createAll() {
    if (creating || chosen.length === 0) return;
    setCreating(true); setProgress(0); setFailedCount(0); setBadDateCount(0);
    stopRef.current = false;

    const okIds = new Set();
    const badDateIds = new Set();
    const failedIds = new Set();
    let done = 0;

    // Snapshot of what to create, by id. Rows are frozen while this runs (the
    // inputs are disabled), so what we send is what the user saw.
    const queue = chosen.map(r => ({ ...r }));

    for (let i = 0; i < queue.length; i += BATCH) {
      if (stopRef.current) break;
      const slice = queue.slice(i, i + BATCH);
      // eslint-disable-next-line no-await-in-loop
      await Promise.all(slice.map(async (r) => {
        const t = r.allDay ? '00:00' : (r.time || '09:00');
        const local = new Date(`${r.date}T${t}:00`);
        if (isNaN(local.getTime())) {
          badDateIds.add(r.id);
        } else {
          try {
            await apiClient.post('/events', {
              title: r.title.trim(),
              datetime: local.toISOString(),
              timezone,
              priority: r.priority,
              allDay: r.allDay,
              location: (r.location && r.location.trim()) || undefined,
              notes: (r.notes && r.notes.trim()) || undefined,
              type: r.type || undefined,
              visibility: (visibility === 'group' && groupId) ? 'group' : 'private',
              groupId:    (visibility === 'group' && groupId) ? groupId : null,
              subCalendarId: subCalendarId || undefined,
            });
            okIds.add(r.id);
          } catch (_) {
            failedIds.add(r.id);
          }
        }
        done++; setProgress(done);
      }));
    }

    setCreating(false);
    const stopped = stopRef.current;
    stopRef.current = false;

    if (!stopped && badDateIds.size === 0 && failedIds.size === 0) {
      onCreated();
      return;
    }
    // Keep only what still has to be created — matched by id, so a row the user
    // touched mid-save is still recognised and never created a second time.
    setRows(rs => rs
      .filter(r => !okIds.has(r.id))
      .map(r => (badDateIds.has(r.id) ? { ...r, error: 'bad_date' }
        : failedIds.has(r.id) ? { ...r, error: 'failed' } : r)));
    setFailedCount(failedIds.size);
    setBadDateCount(badDateIds.size);
  }

  const S = 'var(--color-primary)';
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'var(--color-bg, #F3EEE6)', display: 'flex', flexDirection: 'column', maxWidth: 520, margin: '0 auto' }}>
      {/* Discard confirmation — the only way out that loses the scan */}
      {askDiscard && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 320, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <DialogCard style={{ background: 'var(--color-card)', borderRadius: 'var(--radius, 14px)', padding: '22px 20px', width: '100%', maxWidth: 340, boxShadow: '0 8px 32px rgba(0,0,0,0.22)' }}>
            <p style={{ margin: '0 0 18px', fontWeight: 700, fontSize: '0.95rem', color: 'var(--color-text)' }}>
              {DL.text(rows.length)}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button type="button" className="btn btn-outline" onClick={() => setAskDiscard(false)}>{DL.stay}</button>
              <button type="button" className="btn btn-outline" style={{ borderColor: '#D04535', color: '#D04535' }} onClick={onCancel}>{DL.leave}</button>
            </div>
          </DialogCard>
        </div>
      )}

      {/* Header — the way out is the same green circle as everywhere else,
          not a word among words. While the events are being created it stops
          the run instead: whatever was made stays made, the rest is still
          listed below. */}
      <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <button
          type="button"
          onClick={creating ? () => { stopRef.current = true; } : requestClose}
          aria-label={creating ? stopLabel : T.cancelButton}
          title={creating ? stopLabel : T.cancelButton}
          style={{
            flexShrink: 0, width: 38, height: 38, borderRadius: '50%', padding: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: S, color: '#fff', border: 'none', cursor: 'pointer',
          }}
        >
          <span className="rtl-flip" style={{ display: 'flex' }}><BackIcon /></span>
        </button>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '1.05rem', fontWeight: 800, color: 'var(--color-text)' }}>
            {chosen.length} · {T.multiTitle}
          </div>
          <div style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)', marginTop: 1 }}>{T.multiSubtitle}</div>
        </div>
      </div>

      {/* List */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px 12px' }}>
        {rows.map(r => (
          <Row
            key={r.id}
            row={r}
            hints={hints}
            T={T}
            A={A}
            disabled={creating}
            onPatch={patch}
            onRemove={remove}
            onCyclePrio={cyclePrio}
          />
        ))}
      </div>

      {/* What went wrong — the two causes are told apart, because the cure is
          completely different: wait for signal vs. fix a date on one row */}
      {badDateCount > 0 && !creating && (
        <div style={{ margin: '0 14px 8px', padding: '10px 12px', borderRadius: 10, background: 'rgba(208,69,53,0.09)', border: '1.5px solid rgba(208,69,53,0.5)', color: '#c0392b', fontSize: '0.85rem', lineHeight: 1.4 }}>
          {badDateMsg(badDateCount)}
        </div>
      )}
      {failedCount > 0 && !creating && (
        <div style={{ margin: '0 14px 8px', padding: '10px 12px', borderRadius: 10, background: 'rgba(208,69,53,0.09)', border: '1.5px solid rgba(208,69,53,0.5)', color: '#c0392b', fontSize: '0.85rem', lineHeight: 1.4 }}>
          {failedMsg(failedCount)}
        </div>
      )}

      {/* Footer — Silvia's layout: a round green OK, and beside it the
          calendars, each rectangle only as wide as its own name.
          Her arrangement is better than the one I had: the calendars sit ON
          the same line as the button that acts, so "where does this go" and
          "go" are read together instead of one above the other. */}
      <div style={{
        padding: '10px 12px calc(12px + env(safe-area-inset-bottom, 0px))',
        borderTop: '1px solid var(--color-border)',
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      }}>
        {/* OK — round, green, the twin of the one on the event form */}
        <button
          type="button"
          onClick={creating ? () => { stopRef.current = true; } : createAll}
          disabled={!creating && chosen.length === 0}
          aria-label={creating ? stopLabel : T.multiCreate}
          title={creating ? stopLabel : T.multiCreate}
          style={{
            flexShrink: 0, width: 58, height: 58, borderRadius: '50%', padding: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: 'none', background: chosen.length === 0 && !creating ? 'var(--color-border)' : S,
            color: '#fff', fontWeight: 800, fontSize: creating ? '0.8rem' : '1.12rem',
            fontFamily: 'inherit', lineHeight: 1.1,
            cursor: (!creating && chosen.length === 0) ? 'not-allowed' : 'pointer',
          }}
        >
          {creating ? `${progress}/${chosen.length}` : <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true" stroke="currentColor" fill="none" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4.5 4.5L19 7"/></svg>}
        </button>

        {/* Where they go. Each button is as wide as its name and no wider —
            a calendar called "Casa" has no business being as big as one
            called "Allenamenti di pallavolo". */}
        {groups.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, flex: 1, minWidth: 0 }}>
            {[
              { key: 'private', label: (userName || '').split(' ')[0] || (T.form?.visibilityPrivate || 'Io'), on: visibility === 'private' },
              ...groups.map(g => ({ key: g.id, label: g.name, on: visibility === 'group' && groupId === g.id })),
            ].map(opt => (
              <button
                key={opt.key}
                type="button"
                disabled={creating}
                aria-pressed={opt.on}
                onClick={() => {
                  if (opt.key === 'private') { setVisibility('private'); return; }
                  setVisibility('group'); setGroupId(opt.key);
                }}
                style={{
                  flex: '0 0 auto', maxWidth: '100%',
                  padding: '9px 14px', fontSize: '0.82rem', fontWeight: 600,
                  fontFamily: 'inherit',
                  borderRadius: 'var(--radius-sm, 10px)',
                  border: `1.5px solid ${S}`,
                  background: opt.on ? S : 'transparent',
                  color: opt.on ? '#fff' : 'var(--color-text)',
                  cursor: creating ? 'not-allowed' : 'pointer',
                  opacity: creating ? 0.5 : 1,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {/* And which label — written exactly as in the event form: the name
            in its own colour, no box around it, underlined when chosen.
            No "none" button: tapping the chosen one lets it go, which is how
            the form has always done it and one button fewer to read. */}
        {subCalendars.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, width: '100%', alignItems: 'center' }}>
            {subCalendars.map(sc => (
              <button
                key={sc.id}
                type="button"
                disabled={creating}
                aria-pressed={subCalendarId === sc.id}
                onClick={() => setSubCalendarId(id => (id === sc.id ? '' : sc.id))}
                style={{
                  background: 'none', border: 'none', padding: '4px 6px',
                  cursor: creating ? 'not-allowed' : 'pointer',
                  fontSize: '0.75rem', fontWeight: 700, fontFamily: 'inherit',
                  textTransform: 'uppercase', letterSpacing: '0.04em',
                  color: sc.color,
                  textDecoration: subCalendarId === sc.id ? 'underline' : 'none',
                  textUnderlineOffset: '3px',
                  opacity: creating ? 0.5 : 1,
                }}
              >
                {sc.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
